import { useRef } from 'react';
import { File as FileIcon, Image } from 'lucide-react';
import { cn } from '@/lib/utils';
import { FloatingOverlay } from '@/components/ui/floating-overlay';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type AttachmentCategory = 'media' | 'general';

export interface AttachmentMenuProps {
  /** Ref to the button that opens this menu (used for anchor positioning). */
  anchorRef: React.RefObject<HTMLButtonElement | null>;
  open: boolean;
  onClose: () => void;
  /** Called with the raw File objects and their intended category. */
  onFilesSelected: (files: File[], category: AttachmentCategory) => void;
}

// ---------------------------------------------------------------------------
// Menu items configuration
// ---------------------------------------------------------------------------

interface MenuItem {
  label: string;
  description: string;
  accept: string;
  category: AttachmentCategory;
  multiple: boolean;
  icon: React.ElementType;
}

const MENU_ITEMS: MenuItem[] = [
  {
    label: 'Photo or Video',
    description: 'Share an image or video clip',
    accept: 'image/*,video/*',
    category: 'media',
    multiple: true,
    icon: Image,
  },
  {
    label: 'File',
    description: 'Any document or file type',
    accept: '*/*',
    category: 'general',
    multiple: true,
    icon: FileIcon,
  },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AttachmentMenu({ anchorRef, open, onClose, onFilesSelected }: AttachmentMenuProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingItemRef = useRef<MenuItem | null>(null);

  const openFilePicker = (item: MenuItem) => {
    if (!fileInputRef.current) return;
    pendingItemRef.current = item;
    fileInputRef.current.accept = item.accept;
    fileInputRef.current.multiple = item.multiple;
    // Reset so the same file can be re-selected after removal
    fileInputRef.current.value = '';
    fileInputRef.current.click();
    onClose();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    const item = pendingItemRef.current;
    if (files.length === 0 || !item) return;
    onFilesSelected(files, item.category);
    pendingItemRef.current = null;
  };

  return (
    <>
      {/* Hidden file input — shared across all menu items */}
      <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileChange} />

      <FloatingOverlay
        open={open}
        onClose={onClose}
        anchorRef={anchorRef as React.RefObject<HTMLElement | null>}
        preferredPlacement="top"
        offset={8}
      >
        <div className="min-w-[200px] rounded-xl border border-border bg-card shadow-[0_8px_32px_rgba(0,0,0,0.12)] overflow-hidden">
          <div className="p-1">
            {MENU_ITEMS.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.label}
                  type="button"
                  onClick={() => openFilePicker(item)}
                  className={cn(
                    'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left',
                    'transition-colors hover:bg-muted/70 active:bg-muted',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  )}
                >
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted flex-shrink-0">
                    <Icon className="h-4 w-4 text-foreground/70" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[13px] font-medium text-foreground leading-tight">
                      {item.label}
                    </span>
                    <span className="block text-[11px] text-muted-foreground leading-tight mt-0.5">
                      {item.description}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </FloatingOverlay>
    </>
  );
}
